import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ConfiguratorHeader from '../components/ConfiguratorHeader';
import Building3DPreview from '../components/Building3DPreview';
import Building4ViewElevation from '../components/Building4ViewElevation';
import ConfiguratorSidebar from '../components/ConfiguratorSidebar';
import { buildingStyleProfiles as defaultProfiles } from '../data/configuratorMock';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Printer, UserPlus, Search, X, Check, Save, Box, LayoutGrid, Square, Ruler, Home, DoorOpen, Grid2x2, Palette, Settings2, Layers } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConfiguratorLanding = () => {
  const { user, token, isAuthenticated, loading: authLoading, isCustomer } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check if user is a customer (for showing "Save My Quote" button)
  const isCustomerUser = isAuthenticated && isCustomer && isCustomer();
  
  // View mode state (3D or 2D)
  const [viewMode, setViewMode] = useState('3d'); // '3d' or '2d'
  
  // Edit mode state
  const [editOpportunityId, setEditOpportunityId] = useState(null);
  const [editOpportunity, setEditOpportunity] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Configurator settings from backend (building styles, colors, etc.)
  const [configuratorSettings, setConfiguratorSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  // Use settings from backend or fallback to defaults
  const buildingStyleProfiles = useMemo(() => {
    if (configuratorSettings?.building_styles) {
      // Convert array to object keyed by id
      const profiles = {};
      configuratorSettings.building_styles.forEach(style => {
        profiles[style.id] = {
          id: style.id,
          name: style.name,
          description: style.description || '',
          defaults: style.defaults || defaultProfiles[style.id]?.defaults || defaultProfiles.carport.defaults
        };
      });
      return profiles;
    }
    return defaultProfiles;
  }, [configuratorSettings]);
  
  // Fetch configurator settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/configurator-settings`);
        setConfiguratorSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch configurator settings, using defaults:', error);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);
  
  // Check if user can see pricing (sales role or higher)
  const canSeePricing = useMemo(() => {
    if (authLoading) return false;
    if (!isAuthenticated || !user) return false;
    // Check for any of these permissions that indicate sales/admin role
    const salesPermissions = ['perm_leads_read', 'perm_users_read', 'perm_roles_read', 'perm_pricing_read'];
    return user.permissions?.some(p => salesPermissions.includes(p)) || false;
  }, [user, isAuthenticated, authLoading]);
  
  // Initialize with carport profile defaults
  const defaultProfile = buildingStyleProfiles.carport || defaultProfiles.carport;
  
  const [config, setConfig] = useState({
    style: 'carport',
    installationSurface: 'soil',
    width: defaultProfile.defaults.width,
    length: defaultProfile.defaults.length,
    legHeight: defaultProfile.defaults.legHeight,
    roofStyle: defaultProfile.defaults.roofStyle,
    roofPitch: defaultProfile.defaults.roofPitch,
    roofColor: '#C41E3A',
    trimColor: '#FFFFFF',
    sideColor: '#FFFFFF',
    wainscotColor: 'none',
    // Enclosures from profile
    leftEnd: defaultProfile.defaults.leftEnd,
    rightEnd: defaultProfile.defaults.rightEnd,
    frontSide: defaultProfile.defaults.frontSide,
    backSide: defaultProfile.defaults.backSide,
    // Partial enclosure settings (for shop port style)
    enclosedLength: defaultProfile.defaults.enclosedLength || 0,
    enclosedSide: defaultProfile.defaults.enclosedSide || 'left',
    // Lean-to options
    leftLean: 'none',
    rightLean: 'none',
    // Lean-to dimensions
    leftLeanWidth: 10,
    leftLeanLength: 21,
    leftLeanHeight: 8,
    rightLeanWidth: 10,
    rightLeanLength: 21,
    rightLeanHeight: 8,
    // Frame & Siding options
    sidingOrientation: 'horizontal',
    // Roof rib direction: 'vertical' = down slope (default), 'horizontal' = along ridge
    roofRibDirection: 'vertical',
    asphaltAnchors: 0,
    concreteAnchors: 0,
    mobileAnchors: 0,
    // Doors and Windows - array of objects with {id, type, wall, position, width, height}
    garageDoors: [],
    walkDoors: [],
    windows: [],
    // Services
    services: {
      buildOverExisting: false,
      cutLegsOnSite: false,
      installOverExisting: false,
      installOnDeck: false,
      installOnWall: false,
    },
  });

  // Pricing state
  const [pricing, setPricing] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [buildingEstimate, setBuildingEstimate] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('style');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [savingMyQuote, setSavingMyQuote] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [showCustomerResult, setShowCustomerResult] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    zipCode: '',
    notes: ''
  });

  // Fetch pricing data when user auth is ready and can see pricing
  useEffect(() => {
    if (canSeePricing && !pricing) {
      fetchPricing();
    }
  }, [canSeePricing, pricing]);

  // Calculate price whenever config or pricing changes
  useEffect(() => {
    if (canSeePricing && pricing) {
      calculatePrice();
    }
  }, [config, pricing, canSeePricing]);

  // Load opportunity for editing if edit param is present
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && token && !editOpportunity) {
      setEditOpportunityId(editId);
      loadOpportunityForEdit(editId);
    }
  }, [searchParams, token]);

  const loadOpportunityForEdit = async (opportunityId) => {
    try {
      let response;
      
      // If user is a customer, load from portal API, otherwise use staff API
      if (isCustomerUser) {
        // For customers, fetch from their estimates list
        response = await axios.get(`${BACKEND_URL}/api/portal/my-estimates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const estimates = response.data;
        const opp = estimates.find(e => e.id === opportunityId);
        if (!opp) {
          toast.error('Quote not found');
          return;
        }
        setEditOpportunity(opp);
        
        // Load configuration into state
        if (opp.configuration) {
          setConfig(prev => ({
            ...prev,
            ...opp.configuration
          }));
          toast.success(`Loaded quote for editing: ${opp.title}`);
        }
      } else {
        // For staff, use the opportunities API
        response = await axios.get(`${BACKEND_URL}/api/opportunities/${opportunityId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const opp = response.data;
        setEditOpportunity(opp);
        
        // Load configuration into state
        if (opp.configuration) {
          setConfig(prev => ({
            ...prev,
            ...opp.configuration
          }));
          toast.success(`Loaded quote for editing: ${opp.title}`);
        }
      }
    } catch (error) {
      console.error('Failed to load opportunity:', error);
      toast.error('Failed to load quote for editing');
    }
  };

  const handleSaveEditedQuote = async () => {
    if (!editOpportunityId) return;
    
    setIsSaving(true);
    try {
      const configData = {
        configuration: {
          ...config,
          buildingEstimate,
          pricing: calculatedPrice
        }
      };
      
      // Use different API based on user type
      if (isCustomerUser) {
        await axios.put(
          `${BACKEND_URL}/api/portal/my-estimates/${editOpportunityId}`,
          configData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Quote updated successfully!');
        navigate('/portal');
      } else {
        await axios.put(
          `${BACKEND_URL}/api/opportunities/${editOpportunityId}/configuration`,
          {
            ...configData,
            notes: `Configuration updated via configurator`
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Quote updated successfully!');
        
        // Navigate back to customer profile if we came from there
        if (editOpportunity?.customer_id) {
          navigate(`/crm/customers/${editOpportunity.customer_id}`);
        }
      }
    } catch (error) {
      console.error('Failed to save quote:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  const calculatePrice = () => {
    if (!pricing) return;
    
    setPriceLoading(true);
    
    // Find style price
    const stylePrice = pricing.style_prices.find(s => s.style_id === config.style) 
      || pricing.style_prices[0];
    
    // Calculate base price
    const sqft = config.width * config.length;
    let basePrice = stylePrice.base_price + (sqft * stylePrice.price_per_sqft);
    
    // Build line items
    const lineItems = [
      { name: `${stylePrice.style_name} Base`, price: stylePrice.base_price },
      { name: `Size (${config.width}' × ${config.length}' = ${sqft} sqft)`, price: sqft * stylePrice.price_per_sqft }
    ];
    
    let featuresTotal = 0;
    
    // Wainscot (36" panel on lower walls) - priced per linear foot
    if (config.wainscotColor && config.wainscotColor !== 'none') {
      // Calculate linear feet of enclosed walls
      let linearFeet = 0;
      if (config.leftEnd === 'enclosed') linearFeet += config.width;
      if (config.rightEnd === 'enclosed') linearFeet += config.width;
      if (config.frontSide === 'enclosed') linearFeet += config.length;
      if (config.backSide === 'enclosed') linearFeet += config.length;
      
      const wainscotFeature = pricing.feature_prices.find(f => f.feature_id === 'wainscot_36');
      const pricePerFt = wainscotFeature?.price || 4.50;
      const wainscotPrice = linearFeet * pricePerFt;
      
      if (linearFeet > 0) {
        lineItems.push({ name: `36" Wainscot (${linearFeet} lin.ft)`, price: wainscotPrice });
        featuresTotal += wainscotPrice;
      }
    }
    
    // Garage doors
    config.garageDoors.forEach(door => {
      const doorSize = `${door.width}x${door.height}`;
      const doorFeature = pricing.feature_prices.find(f => 
        f.category === 'door' && f.feature_id.includes(doorSize.replace('x', 'x'))
      );
      const doorPrice = doorFeature?.price || 950;
      lineItems.push({ name: `Garage Door ${doorSize}`, price: doorPrice });
      featuresTotal += doorPrice;
    });
    
    // Walk doors
    config.walkDoors.forEach(() => {
      const walkDoorFeature = pricing.feature_prices.find(f => f.feature_id === 'walk_door_3x7');
      const doorPrice = walkDoorFeature?.price || 350;
      lineItems.push({ name: 'Walk-In Door 3\'×7\'', price: doorPrice });
      featuresTotal += doorPrice;
    });
    
    // Windows
    config.windows.forEach(window => {
      const winSize = `${Math.round(window.width)}x${Math.round(window.height)}`;
      const winFeature = pricing.feature_prices.find(f => 
        f.category === 'window' && f.feature_id.includes(winSize)
      );
      const winPrice = winFeature?.price || 275;
      lineItems.push({ name: `Window ${winSize}`, price: winPrice });
      featuresTotal += winPrice;
    });
    
    // Services
    if (config.services) {
      if (config.services.buildOverExisting) {
        const svcFeature = pricing.feature_prices.find(f => f.feature_id === 'build_over_existing');
        const svcPrice = svcFeature?.price || 250;
        lineItems.push({ name: 'Build Over Existing Structure', price: svcPrice });
        featuresTotal += svcPrice;
      }
      if (config.services.cutLegsOnSite) {
        const svcFeature = pricing.feature_prices.find(f => f.feature_id === 'cut_legs_on_site');
        const svcPrice = svcFeature?.price || 150;
        lineItems.push({ name: 'Cut Legs on Site', price: svcPrice });
        featuresTotal += svcPrice;
      }
      if (config.services.installOverExisting) {
        const svcFeature = pricing.feature_prices.find(f => f.feature_id === 'install_over_existing');
        const svcPrice = svcFeature?.price || 350;
        lineItems.push({ name: 'Install Over Existing Structure', price: svcPrice });
        featuresTotal += svcPrice;
      }
      if (config.services.installOnDeck) {
        const svcFeature = pricing.feature_prices.find(f => f.feature_id === 'install_on_deck');
        const svcPrice = svcFeature?.price || 400;
        lineItems.push({ name: 'Install on Deck or Dock', price: svcPrice });
        featuresTotal += svcPrice;
      }
      if (config.services.installOnWall) {
        const svcFeature = pricing.feature_prices.find(f => f.feature_id === 'install_on_wall');
        const svcPrice = svcFeature?.price || 300;
        lineItems.push({ name: 'Install on Wall (Up to 3\')', price: svcPrice });
        featuresTotal += svcPrice;
      }
    }
    
    const total = basePrice + featuresTotal;
    
    setCalculatedPrice({
      basePrice: Math.round(basePrice * 100) / 100,
      featuresTotal: Math.round(featuresTotal * 100) / 100,
      total: Math.round(total * 100) / 100,
      lineItems,
      sqft
    });
    
    // Calculate building estimate with rates (discount, tax, deposit)
    const rates = pricing.rates || {
      manufacturer_discount_percent: 10,
      sales_tax_percent: 7,
      deposit_percent: 17
    };
    
    const discountAmount = total * (rates.manufacturer_discount_percent / 100);
    const subtotal = total - discountAmount;
    const taxAmount = subtotal * (rates.sales_tax_percent / 100);
    const grandTotal = subtotal + taxAmount;
    const depositAmount = grandTotal * (rates.deposit_percent / 100);
    const dueOnDelivery = grandTotal - depositAmount;
    
    setBuildingEstimate({
      buildingEstimate: Math.round(total * 100) / 100,
      discountPercent: rates.manufacturer_discount_percent,
      discountAmount: Math.round(discountAmount * 100) / 100,
      subtotal: Math.round(subtotal * 100) / 100,
      taxPercent: rates.sales_tax_percent,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(grandTotal * 100) / 100,
      depositPercent: rates.deposit_percent,
      depositAmount: Math.round(depositAmount * 100) / 100,
      dueOnDelivery: Math.round(dueOnDelivery * 100) / 100
    });
    
    setPriceLoading(false);
  };

  // Handle config changes, with special handling for style selection
  const handleConfigChange = useCallback((key, value) => {
    console.log('Config change:', key, value);
    
    if (key === 'style') {
      // When style changes, apply the full profile defaults
      const profile = buildingStyleProfiles[value];
      if (profile) {
        setConfig(prev => ({
          ...prev,
          style: value,
          width: profile.defaults.width,
          length: profile.defaults.length,
          legHeight: profile.defaults.legHeight,
          roofStyle: profile.defaults.roofStyle,
          roofPitch: profile.defaults.roofPitch || 3,
          leftEnd: profile.defaults.leftEnd,
          rightEnd: profile.defaults.rightEnd,
          frontSide: profile.defaults.frontSide,
          backSide: profile.defaults.backSide,
          enclosedLength: profile.defaults.enclosedLength || 0,
          enclosedSide: profile.defaults.enclosedSide || 'left',
          // Apply lean-to defaults from profile
          leftLean: profile.defaults.leftLean || 'none',
          leftLeanWidth: profile.defaults.leftLeanWidth || 10,
          leftLeanLength: profile.defaults.leftLeanLength || prev.length,
          leftLeanHeight: profile.defaults.leftLeanHeight || 8,
          rightLean: profile.defaults.rightLean || 'none',
          rightLeanWidth: profile.defaults.rightLeanWidth || 10,
          rightLeanLength: profile.defaults.rightLeanLength || prev.length,
          rightLeanHeight: profile.defaults.rightLeanHeight || 8,
          garageDoors: (profile.defaults.garageDoors || []).map((d, i) => ({ ...d, id: Date.now() + i })),
          walkDoors: (profile.defaults.walkDoors || []).map((d, i) => ({ ...d, id: Date.now() + 1000 + i })),
          windows: (profile.defaults.windows || []).map((w, i) => ({ ...w, id: Date.now() + 2000 + i })),
        }));
        toast.success(`Loaded ${profile.name} profile`);
      }
    } else {
      setConfig(prev => ({ ...prev, [key]: value }));
    }
  }, []);

  // Add a garage door
  const handleAddGarageDoor = useCallback((doorType, wall) => {
    const doorSizes = {
      '10x8': { width: 10, height: 8 },
      '9x7': { width: 9, height: 7 },
      '12x10': { width: 12, height: 10 },
      '8x8': { width: 8, height: 8 },
      '10x10': { width: 10, height: 10 },
    };
    const size = doorSizes[doorType] || { width: 10, height: 8 };
    
    setConfig(prev => ({
      ...prev,
      garageDoors: [...prev.garageDoors, {
        id: Date.now(),
        type: doorType,
        wall: wall,
        position: 50, // percentage from left (0-100)
        width: size.width,
        height: size.height,
      }]
    }));
    toast.success(`Added ${doorType} garage door to ${wall} wall`);
  }, []);

  // Add a walk door
  const handleAddWalkDoor = useCallback((wall) => {
    setConfig(prev => ({
      ...prev,
      walkDoors: [...prev.walkDoors, {
        id: Date.now(),
        wall: wall,
        position: 50,
        width: 3,
        height: 6.5,
      }]
    }));
    toast.success(`Added walk-in door to ${wall} wall`);
  }, []);

  // Add a window
  const handleAddWindow = useCallback((windowType, wall) => {
    const windowSizes = {
      '30x30': { width: 2.5, height: 2.5 },
      '36x36': { width: 3, height: 3 },
      '24x36': { width: 2, height: 3 },
      '48x36': { width: 4, height: 3 },
    };
    const size = windowSizes[windowType] || { width: 3, height: 3 };
    
    setConfig(prev => ({
      ...prev,
      windows: [...prev.windows, {
        id: Date.now(),
        type: windowType,
        wall: wall,
        position: 50,
        heightPos: 50, // percentage from bottom
        width: size.width,
        height: size.height,
      }]
    }));
    toast.success(`Added ${windowType} window to ${wall} wall`);
  }, []);

  // Remove an item
  const handleRemoveItem = useCallback((itemType, itemId) => {
    setConfig(prev => ({
      ...prev,
      [itemType]: prev[itemType].filter(item => item.id !== itemId)
    }));
    toast.success('Item removed');
  }, []);

  // Update item position
  const handleUpdateItemPosition = useCallback((itemType, itemId, newPosition) => {
    setConfig(prev => ({
      ...prev,
      [itemType]: prev[itemType].map(item => 
        item.id === itemId ? { ...item, position: newPosition } : item
      )
    }));
  }, []);
  
  // Clone an item (duplicate with slightly different position)
  const handleCloneItem = useCallback((itemType, itemId) => {
    setConfig(prev => {
      const items = prev[itemType] || [];
      const itemToClone = items.find(item => item.id === itemId);
      if (!itemToClone) return prev;
      
      // Create a new item with offset position
      const newItem = {
        ...itemToClone,
        id: Date.now(),
        position: Math.min(90, itemToClone.position + 15) // Offset by 15%
      };
      
      return {
        ...prev,
        [itemType]: [...items, newItem]
      };
    });
    toast.success('Item duplicated');
  }, []);
  
  // Change item size
  const handleChangeItemSize = useCallback((itemType, itemId, newWidth, newHeight) => {
    setConfig(prev => ({
      ...prev,
      [itemType]: prev[itemType].map(item => 
        item.id === itemId ? { ...item, width: newWidth, height: newHeight } : item
      )
    }));
    toast.success(`Size changed to ${newWidth}' × ${newHeight}'`);
  }, []);

  const handleGetQuote = () => {
    setShowQuoteModal(true);
  };

  // Print Quote - Generate PDF for salespeople
  const [printingQuote, setPrintingQuote] = useState(false);
  
  // View Details modal state
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
  
  // Send to Customer state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sendingToCustomer, setSendingToCustomer] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);

  // Fetch customers when modal opens
  const fetchCustomers = async (search = '') => {
    setCustomersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = search 
        ? `${BACKEND_URL}/api/customers/?search=${encodeURIComponent(search)}`
        : `${BACKEND_URL}/api/customers/`;
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Handle opening send to customer modal
  const handleOpenCustomerModal = () => {
    setShowCustomerModal(true);
    setSelectedCustomer(null);
    setCustomerSearch('');
    fetchCustomers();
  };

  // Handle customer search
  const handleCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    // Debounce search
    clearTimeout(window.customerSearchTimeout);
    window.customerSearchTimeout = setTimeout(() => {
      fetchCustomers(value);
    }, 300);
  };

  // Send estimate to selected customer
  const handleSendToCustomer = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingToCustomer(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create an opportunity for this customer with the configuration
      const opportunityData = {
        title: `Building Quote - ${config.style} ${config.width}'×${config.length}'`,
        description: `${config.style} building configuration`,
        value: calculatedPrice?.total || 0,
        customer_id: selectedCustomer.id,
        configuration: {
          ...config,
          pricing: calculatedPrice || null,
          buildingEstimate: buildingEstimate || null
        },
        contact_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`,
        contact_email: selectedCustomer.email,
        contact_phone: selectedCustomer.phone || '',
        source: 'configurator'
      };

      await axios.post(`${BACKEND_URL}/api/opportunities/`, opportunityData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success(`Estimate sent to ${selectedCustomer.first_name} ${selectedCustomer.last_name}'s profile`);
      setShowCustomerModal(false);
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Failed to send to customer:', error);
      toast.error('Failed to send estimate to customer');
    } finally {
      setSendingToCustomer(false);
    }
  };
  
  const handlePrintQuote = async () => {
    if (!canSeePricing) {
      toast.error('Please log in to print quotes');
      return;
    }
    
    setPrintingQuote(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/leads/generate-pdf`,
        {
          configuration: {
            ...config,
            pricing: calculatedPrice || null,
            buildingEstimate: buildingEstimate || null
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FusionBuilderPro_Quote_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Quote PDF downloaded!');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setPrintingQuote(false);
    }
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    
    try {
      // Include full configuration with pricing and building estimate
      const quoteData = {
        name: quoteForm.name,
        email: quoteForm.email,
        phone: quoteForm.phone,
        zip_code: quoteForm.zipCode,
        notes: quoteForm.notes,
        configuration: {
          ...config,
          pricing: calculatedPrice || null,
          buildingEstimate: buildingEstimate || null
        }
      };
      
      await axios.post(`${BACKEND_URL}/api/leads/submit-quote`, quoteData);
      
      toast.success('Quote request submitted! We will contact you shortly.');
      setShowQuoteModal(false);
      setQuoteForm({ name: '', email: '', phone: '', zipCode: '', notes: '' });
    } catch (error) {
      console.error('Failed to submit quote:', error);
      toast.error('Failed to submit quote. Please try again.');
    }
  };

  // Save quote to customer's own portal (for logged-in customers)
  const handleSaveMyQuote = async () => {
    if (!isAuthenticated || !token) {
      toast.error('Please log in to save your quote');
      return;
    }

    setSavingMyQuote(true);
    try {
      const quoteData = {
        title: `Building Quote - ${config.style} ${config.width}'×${config.length}'`,
        configuration: {
          ...config,
          pricing: calculatedPrice || null,
          buildingEstimate: buildingEstimate || null
        },
        value: calculatedPrice?.total || 0
      };

      await axios.post(`${BACKEND_URL}/api/portal/save-quote`, quoteData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Quote saved to your account! View it in your portal.');
    } catch (error) {
      console.error('Failed to save quote:', error);
      toast.error('Failed to save quote. Please try again.');
    } finally {
      setSavingMyQuote(false);
    }
  };

  // Create customer account and attach quote (staff only)
  const handleCreateCustomerWithQuote = async () => {
    if (!isAuthenticated || !token) {
      toast.error('Please log in to create customer accounts');
      return;
    }

    // Validate required fields
    if (!quoteForm.name || !quoteForm.email || !quoteForm.phone) {
      toast.error('Please fill in Name, Email, and Phone');
      return;
    }

    // Parse name into first/last
    const nameParts = quoteForm.name.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    setCreatingCustomer(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/leads/create-customer-with-quote`,
        {
          first_name: firstName,
          last_name: lastName,
          email: quoteForm.email,
          phone: quoteForm.phone,
          zip_code: quoteForm.zipCode,
          notes: quoteForm.notes,
          configuration: {
            ...config,
            pricing: calculatedPrice || null,
            buildingEstimate: buildingEstimate || null
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Show success with temp password
      setShowCustomerResult(response.data);
      toast.success(response.data.customer_created 
        ? 'Customer created and quote attached!' 
        : 'Quote attached to existing customer!'
      );
    } catch (error) {
      console.error('Failed to create customer with quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to create customer. Please try again.');
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Get current profile for dimension constraints
  const currentProfile = buildingStyleProfiles[config.style] || buildingStyleProfiles.carport;

  // Helper function to get enclosure display name
  const getEnclosureLabel = (value) => {
    const labels = {
      'open': 'Open',
      'enclosed': 'Fully Enclosed',
      'top-3': 'Top 3ft Enclosed',
      'bottom-3': 'Bottom 3ft Enclosed',
    };
    return labels[value] || value;
  };

  // Helper function to get roof style display name
  const getRoofStyleLabel = (style) => {
    const labels = {
      'regular': 'Regular (Rounded)',
      'gable': 'A-Frame/Boxed Eave',
      'vertical': 'Vertical Roof',
    };
    return labels[style] || style;
  };

  return (
    <div className="h-screen bg-gray-100 dark:bg-gray-950 flex flex-col overflow-hidden transition-colors duration-300">
      <ConfiguratorHeader onViewDetails={() => setShowViewDetailsModal(true)} />
      
      <div className="flex-1 flex overflow-hidden">
        {/* Preview Area */}
        <div className="flex-1 relative overflow-hidden">
          {/* View Toggle - Top Right */}
          <div className="absolute top-4 right-4 z-20">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1 flex gap-1">
              <button
                onClick={() => setViewMode('3d')}
                data-testid="view-3d-btn"
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === '3d'
                    ? 'bg-[#C41E3A] text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Box className="w-4 h-4" />
                3D View
              </button>
              <button
                onClick={() => setViewMode('2d')}
                data-testid="view-2d-btn"
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === '2d'
                    ? 'bg-[#C41E3A] text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                4-View
              </button>
            </div>
          </div>
          
          {/* Price Display - Only shown for sales roles */}
          {canSeePricing && calculatedPrice && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
              <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-xl shadow-lg dark:shadow-gray-900/50 px-6 py-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Estimated Price</p>
                    <p className="text-3xl font-bold text-[#C41E3A]">
                      ${calculatedPrice.total.toLocaleString()}
                    </p>
                  </div>
                  <div className="border-l border-gray-200 dark:border-gray-700 pl-4 text-xs text-gray-500 dark:text-gray-400">
                    <p>Base: ${calculatedPrice.basePrice.toLocaleString()}</p>
                    <p>Features: ${calculatedPrice.featuresTotal.toLocaleString()}</p>
                    <p>{calculatedPrice.sqft} sqft</p>
                  </div>
                  <div className="border-l border-gray-200 dark:border-gray-700 pl-4 flex gap-2">
                    <button
                      onClick={handleOpenCustomerModal}
                      data-testid="send-to-customer-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <UserPlus className="w-4 h-4" />
                      Send to Customer
                    </button>
                    {editOpportunityId && (
                      <button
                        onClick={handleSaveEditedQuote}
                        disabled={isSaving}
                        data-testid="save-edited-quote-btn"
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Save className="w-4 h-4" />
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    )}
                    <button
                      onClick={handlePrintQuote}
                      disabled={printingQuote}
                      data-testid="print-quote-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-[#C41E3A] text-white rounded-lg hover:bg-[#a01830] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Printer className="w-4 h-4" />
                      {printingQuote ? 'Generating...' : 'Print Quote'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Customer "Save My Quote" Button - Only shown for logged-in customers */}
          {isCustomerUser && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
              <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-xl shadow-lg dark:shadow-gray-900/50 px-6 py-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {editOpportunityId 
                        ? 'Make changes and save your updated design'
                        : 'Configure your building and save it to your account'
                      }
                    </p>
                  </div>
                  {editOpportunityId ? (
                    <button
                      onClick={handleSaveEditedQuote}
                      disabled={isSaving}
                      data-testid="save-edited-quote-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  ) : (
                    <button
                      onClick={handleSaveMyQuote}
                      disabled={savingMyQuote}
                      data-testid="save-my-quote-btn"
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {savingMyQuote ? 'Saving...' : 'Save My Quote'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Edit mode indicator */}
          {editOpportunity && (
            <div className="absolute top-4 left-4 z-10 bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
              <Save className="w-4 h-4" />
              <span className="font-medium">Editing: {editOpportunity.title}</span>
              <button 
                onClick={() => {
                  setEditOpportunityId(null);
                  setEditOpportunity(null);
                  navigate(isCustomerUser ? '/portal' : '/');
                }}
                className="ml-2 text-white hover:text-amber-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* 3D View */}
          {viewMode === '3d' && (
            <Building3DPreview 
              config={config} 
              onUpdateItemPosition={handleUpdateItemPosition}
              onDeleteItem={handleRemoveItem}
              onCloneItem={handleCloneItem}
              onChangeItemSize={handleChangeItemSize}
            />
          )}
          
          {/* 2D 4-View Elevation */}
          {viewMode === '2d' && (
            <div className="absolute inset-0 flex items-center justify-center p-6 bg-gray-100 dark:bg-gray-900 overflow-auto">
              <Building4ViewElevation 
                config={config}
                className="max-w-4xl w-full"
              />
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <ConfiguratorSidebar 
          config={config}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onConfigChange={handleConfigChange}
          onAddGarageDoor={handleAddGarageDoor}
          onAddWalkDoor={handleAddWalkDoor}
          onAddWindow={handleAddWindow}
          onRemoveItem={handleRemoveItem}
          onUpdateItemPosition={handleUpdateItemPosition}
          onGetQuote={handleGetQuote}
          currentProfile={currentProfile}
          canSeePricing={canSeePricing}
          buildingEstimate={buildingEstimate}
          configuratorSettings={configuratorSettings}
        />
      </div>

      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">Get Your Quote</h2>
              <button 
                onClick={() => setShowQuoteModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Config Summary */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4 text-sm">
              <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">{currentProfile.name}</p>
              <p className="text-gray-600 dark:text-gray-300">
                {config.width}' W × {config.length}' L × {config.legHeight}' H
              </p>
              {config.garageDoors.length > 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">{config.garageDoors.length} garage door(s)</p>
              )}
              {config.windows.length > 0 && (
                <p className="text-gray-500 dark:text-gray-400 text-xs">{config.windows.length} window(s)</p>
              )}
              {calculatedPrice && (
                <p className="text-green-600 dark:text-green-400 font-semibold mt-1">
                  Estimated: ${calculatedPrice.total?.toLocaleString()}
                </p>
              )}
            </div>
            
            {/* Show result after customer creation */}
            {showCustomerResult ? (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                    ✓ {showCustomerResult.customer_created ? 'Customer Created!' : 'Quote Attached!'}
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                    Quote has been saved to {showCustomerResult.customer_name}'s account.
                  </p>
                  
                  {showCustomerResult.temp_password && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mt-3 border border-green-300 dark:border-green-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Customer Portal Credentials:</p>
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        <strong>Email:</strong> {showCustomerResult.customer_email}
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        <strong>Temp Password:</strong>{' '}
                        <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded font-mono text-red-600 dark:text-red-400">
                          {showCustomerResult.temp_password}
                        </code>
                      </p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                        ⚠️ Share these credentials with the customer so they can access their portal.
                      </p>
                    </div>
                  )}
                  
                  {!showCustomerResult.temp_password && !showCustomerResult.customer_created && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Customer already has portal access.
                    </p>
                  )}
                </div>
                
                <button
                  onClick={() => {
                    setShowCustomerResult(null);
                    setShowQuoteModal(false);
                    setQuoteForm({ name: '', email: '', phone: '', zipCode: '', notes: '' });
                  }}
                  className="w-full py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmitQuote} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={quoteForm.name}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={quoteForm.email}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={quoteForm.phone}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={quoteForm.zipCode}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, zipCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="33601"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Additional Notes</label>
                  <textarea
                    value={quoteForm.notes}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={2}
                    placeholder="Any special requirements..."
                  />
                </div>
                
                {/* Staff: Create Customer & Attach Quote */}
                {isAuthenticated && !isCustomerUser && (
                  <button
                    type="button"
                    onClick={handleCreateCustomerWithQuote}
                    disabled={creatingCustomer}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="create-customer-quote-btn"
                  >
                    {creatingCustomer ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating Customer...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        Create Customer & Send Quote
                      </>
                    )}
                  </button>
                )}
                
                {/* Divider for staff */}
                {isAuthenticated && !isCustomerUser && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">or submit as lead</span>
                    </div>
                  </div>
                )}
                
                <button
                  type="submit"
                  className="w-full py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Submit Quote Request
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Send to Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Send Estimate to Customer
            </DialogTitle>
          </DialogHeader>
          
          {/* Show result after customer creation */}
          {showCustomerResult ? (
            <div className="py-4 space-y-4">
              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                  ✓ {showCustomerResult.customer_created ? 'Customer Created & Quote Sent!' : 'Quote Sent!'}
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                  Quote has been saved to {showCustomerResult.customer_name}'s account.
                </p>
                
                {showCustomerResult.temp_password && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mt-3 border border-green-300 dark:border-green-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Customer Portal Credentials:</p>
                    <p className="text-sm text-gray-800 dark:text-gray-200">
                      <strong>Email:</strong> {showCustomerResult.customer_email}
                    </p>
                    <p className="text-sm text-gray-800 dark:text-gray-200">
                      <strong>Temp Password:</strong>{' '}
                      <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded font-mono text-red-600 dark:text-red-400">
                        {showCustomerResult.temp_password}
                      </code>
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                      ⚠️ Share these credentials with the customer so they can access their portal.
                    </p>
                  </div>
                )}
              </div>
              
              <Button 
                className="w-full"
                onClick={() => {
                  setShowCustomerResult(null);
                  setShowCustomerModal(false);
                  setQuoteForm({ name: '', email: '', phone: '', zipCode: '', notes: '' });
                }}
              >
                Close
              </Button>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              {/* Estimate Summary */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h4 className="font-medium text-sm text-gray-700 dark:text-gray-200 mb-2">Estimate Summary</h4>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                      {config.style} - {config.width}'×{config.length}'
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {config.roofStyle} roof, {config.legHeight}' leg height
                    </p>
                  </div>
                  <p className="text-xl font-bold text-[#C41E3A]">
                    ${calculatedPrice?.total?.toLocaleString() || '0'}
                  </p>
                </div>
              </div>

              {/* Toggle between existing customer and new customer */}
              <div className="flex gap-2 border-b dark:border-gray-700 pb-2">
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    !quoteForm.email 
                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' 
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  Existing Customer
                </button>
                <button
                  onClick={() => setSelectedCustomer('new')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    selectedCustomer === 'new'
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' 
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  + New Customer
                </button>
              </div>

              {selectedCustomer === 'new' ? (
                /* New Customer Form */
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">First Name *</label>
                      <Input
                        value={quoteForm.name.split(' ')[0] || ''}
                        onChange={(e) => {
                          const lastName = quoteForm.name.split(' ').slice(1).join(' ');
                          setQuoteForm(prev => ({ ...prev, name: `${e.target.value} ${lastName}`.trim() }));
                        }}
                        placeholder="John"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Last Name *</label>
                      <Input
                        value={quoteForm.name.split(' ').slice(1).join(' ') || ''}
                        onChange={(e) => {
                          const firstName = quoteForm.name.split(' ')[0] || '';
                          setQuoteForm(prev => ({ ...prev, name: `${firstName} ${e.target.value}`.trim() }));
                        }}
                        placeholder="Smith"
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Email *</label>
                    <Input
                      type="email"
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">Phone *</label>
                    <Input
                      type="tel"
                      value={quoteForm.phone}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">ZIP Code</label>
                    <Input
                      value={quoteForm.zipCode}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, zipCode: e.target.value }))}
                      placeholder="33601"
                      className="text-sm"
                    />
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                    <p className="text-blue-800 dark:text-blue-200">
                      <strong>Note:</strong> A customer portal account will be created automatically. You'll receive temporary login credentials to share with the customer.
                    </p>
                  </div>
                </div>
              ) : (
                /* Existing Customer Search */
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Search Customer
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search by name, email, or company..."
                        value={customerSearch}
                        onChange={handleCustomerSearch}
                        className="pl-10"
                        data-testid="customer-search-input"
                      />
                    </div>
                  </div>

                  {/* Customer List */}
                  <div className="max-h-48 overflow-y-auto border dark:border-gray-700 rounded-lg">
                    {customersLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600"></div>
                      </div>
                    ) : customers.length === 0 ? (
                      <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                        <p>No customers found</p>
                        <button 
                          onClick={() => setSelectedCustomer('new')}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline mt-1"
                        >
                          + Create New Customer
                        </button>
                      </div>
                    ) : (
                      <div className="divide-y dark:divide-gray-700">
                        {customers.map((customer) => (
                          <div
                            key={customer.id}
                            onClick={() => setSelectedCustomer(customer)}
                            className={`p-3 cursor-pointer transition-colors flex items-center justify-between ${
                              selectedCustomer?.id === customer.id
                                ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                            data-testid={`customer-option-${customer.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                  {customer.first_name?.[0]}{customer.last_name?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {customer.first_name} {customer.last_name}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{customer.email}</p>
                                {customer.company && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500">{customer.company}</p>
                                )}
                              </div>
                            </div>
                            {selectedCustomer?.id === customer.id && (
                              <Check className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected Customer Display */}
                  {selectedCustomer && selectedCustomer !== 'new' && (
                    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Selected:</strong> {selectedCustomer.first_name} {selectedCustomer.last_name}
                      </p>
                      <p className="text-xs text-blue-600">{selectedCustomer.email}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!showCustomerResult && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomerModal(false)}>
                Cancel
              </Button>
              {selectedCustomer === 'new' ? (
                <Button 
                  onClick={handleCreateCustomerWithQuote}
                  disabled={creatingCustomer || !quoteForm.name || !quoteForm.email || !quoteForm.phone}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="create-new-customer-btn"
                >
                  {creatingCustomer ? 'Creating...' : 'Create & Send Quote'}
                </Button>
              ) : (
                <Button 
                  onClick={handleSendToCustomer}
                  disabled={!selectedCustomer || sendingToCustomer}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-send-to-customer-btn"
                >
                  {sendingToCustomer ? 'Sending...' : 'Send to Customer'}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={showViewDetailsModal} onOpenChange={setShowViewDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Layers className="w-5 h-5 text-[#C41E3A]" />
              Your Building Configuration
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {/* Building Style Section */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Home className="w-5 h-5 text-[#C41E3A]" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Building Style</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Type</p>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">{currentProfile?.name || config.style}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Installation Surface</p>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">{config.installationSurface}</p>
                </div>
              </div>
            </div>

            {/* Dimensions Section */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Ruler className="w-5 h-5 text-[#C41E3A]" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Dimensions</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white dark:bg-gray-700 rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-[#C41E3A]">{config.width}'</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Width</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-gray-700 rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-[#C41E3A]">{config.length}'</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Length</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-gray-700 rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-[#C41E3A]">{config.legHeight}'</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Leg Height</p>
                </div>
              </div>
              <div className="mt-3 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Total Area: <span className="font-semibold">{config.width * config.length} sq ft</span>
                </p>
              </div>
            </div>

            {/* Roof Section */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Square className="w-5 h-5 text-[#C41E3A]" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Roof</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Roof Style</p>
                  <p className="font-medium text-gray-900 dark:text-white">{getRoofStyleLabel(config.roofStyle)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Roof Pitch</p>
                  <p className="font-medium text-gray-900 dark:text-white">{config.roofPitch}:12</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Rib Direction</p>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">{config.roofRibDirection}</p>
                </div>
              </div>
            </div>

            {/* Colors Section */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Palette className="w-5 h-5 text-[#C41E3A]" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Colors</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg border-2 border-gray-300 shadow-sm" style={{ backgroundColor: config.roofColor }}></div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Roof</p>
                    <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{config.roofColor}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg border-2 border-gray-300 shadow-sm" style={{ backgroundColor: config.trimColor }}></div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Trim</p>
                    <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{config.trimColor}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg border-2 border-gray-300 shadow-sm" style={{ backgroundColor: config.sideColor }}></div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Sides</p>
                    <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{config.sideColor}</p>
                  </div>
                </div>
                {config.wainscotColor && config.wainscotColor !== 'none' && (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg border-2 border-gray-300 shadow-sm" style={{ backgroundColor: config.wainscotColor }}></div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Wainscot</p>
                      <p className="text-xs font-mono text-gray-700 dark:text-gray-300">{config.wainscotColor}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Enclosures Section */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Grid2x2 className="w-5 h-5 text-[#C41E3A]" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Wall Enclosures</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white dark:bg-gray-700 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Left End</p>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{getEnclosureLabel(config.leftEnd)}</p>
                </div>
                <div className="p-2 bg-white dark:bg-gray-700 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Right End</p>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{getEnclosureLabel(config.rightEnd)}</p>
                </div>
                <div className="p-2 bg-white dark:bg-gray-700 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Front Side</p>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{getEnclosureLabel(config.frontSide)}</p>
                </div>
                <div className="p-2 bg-white dark:bg-gray-700 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Back Side</p>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{getEnclosureLabel(config.backSide)}</p>
                </div>
              </div>
            </div>

            {/* Doors & Windows Section */}
            {(config.garageDoors.length > 0 || config.walkDoors.length > 0 || config.windows.length > 0) && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <DoorOpen className="w-5 h-5 text-[#C41E3A]" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Doors & Windows</h3>
                </div>
                <div className="space-y-3">
                  {config.garageDoors.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Garage Doors ({config.garageDoors.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {config.garageDoors.map((door, idx) => (
                          <span key={door.id || idx} className="px-2 py-1 bg-white dark:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300">
                            {door.width}'×{door.height}' on {door.wall}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {config.walkDoors.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Walk-In Doors ({config.walkDoors.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {config.walkDoors.map((door, idx) => (
                          <span key={door.id || idx} className="px-2 py-1 bg-white dark:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300">
                            {door.width}'×{door.height}' on {door.wall}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {config.windows.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Windows ({config.windows.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {config.windows.map((window, idx) => (
                          <span key={window.id || idx} className="px-2 py-1 bg-white dark:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300">
                            {window.type || `${Math.round(window.width * 12)}×${Math.round(window.height * 12)}`}" on {window.wall}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lean-Tos Section */}
            {(config.leftLean !== 'none' || config.rightLean !== 'none') && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-5 h-5 text-[#C41E3A]" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Lean-Tos</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {config.leftLean !== 'none' && (
                    <div className="p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <p className="font-medium text-gray-900 dark:text-white">Left Lean-To</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {config.leftLeanWidth}'W × {config.leftLeanLength}'L × {config.leftLeanHeight}'H
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{config.leftLean}</p>
                    </div>
                  )}
                  {config.rightLean !== 'none' && (
                    <div className="p-3 bg-white dark:bg-gray-700 rounded-lg">
                      <p className="font-medium text-gray-900 dark:text-white">Right Lean-To</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {config.rightLeanWidth}'W × {config.rightLeanLength}'L × {config.rightLeanHeight}'H
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{config.rightLean}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Services Section */}
            {config.services && Object.values(config.services).some(v => v) && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Settings2 className="w-5 h-5 text-[#C41E3A]" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Additional Services</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {config.services.buildOverExisting && (
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm">Build Over Existing</span>
                  )}
                  {config.services.cutLegsOnSite && (
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm">Cut Legs On Site</span>
                  )}
                  {config.services.installOverExisting && (
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm">Install Over Existing</span>
                  )}
                  {config.services.installOnDeck && (
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm">Install On Deck</span>
                  )}
                  {config.services.installOnWall && (
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm">Install On Wall</span>
                  )}
                </div>
              </div>
            )}

            {/* Anchors Section */}
            {(config.asphaltAnchors > 0 || config.concreteAnchors > 0 || config.mobileAnchors > 0) && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Settings2 className="w-5 h-5 text-[#C41E3A]" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Anchoring</h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {config.asphaltAnchors > 0 && (
                    <div className="text-center p-2 bg-white dark:bg-gray-700 rounded-lg">
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{config.asphaltAnchors}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Asphalt Anchors</p>
                    </div>
                  )}
                  {config.concreteAnchors > 0 && (
                    <div className="text-center p-2 bg-white dark:bg-gray-700 rounded-lg">
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{config.concreteAnchors}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Concrete Anchors</p>
                    </div>
                  )}
                  {config.mobileAnchors > 0 && (
                    <div className="text-center p-2 bg-white dark:bg-gray-700 rounded-lg">
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{config.mobileAnchors}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Mobile Anchors</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pricing Summary (if available) */}
            {canSeePricing && calculatedPrice && (
              <div className="bg-[#C41E3A]/10 dark:bg-[#C41E3A]/20 rounded-xl p-4 border border-[#C41E3A]/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Estimated Total</p>
                    <p className="text-3xl font-bold text-[#C41E3A]">${calculatedPrice.total.toLocaleString()}</p>
                  </div>
                  <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                    <p>Base: ${calculatedPrice.basePrice.toLocaleString()}</p>
                    <p>Features: ${calculatedPrice.featuresTotal.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDetailsModal(false)} data-testid="close-view-details-btn">
              Close
            </Button>
            <Button 
              onClick={() => {
                setShowViewDetailsModal(false);
                handleGetQuote();
              }}
              className="bg-[#C41E3A] hover:bg-[#a01830]"
              data-testid="get-quote-from-details-btn"
            >
              Get Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguratorLanding;
